import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styles from "./note-sheet.module.scss";

interface NoteSheetProps {
  habitName: string;
  initialNote: string | null;
  onSave: (note: string | null) => void;
  onClose: () => void;
}

export function NoteSheet({ habitName, initialNote, onSave, onClose }: NoteSheetProps) {
  const [text, setText] = useState(initialNote ?? "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSave = useCallback(() => {
    const trimmed = text.trim();
    onSave(trimmed || null);
    onClose();
  }, [text, onSave, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        handleSave();
      }
    },
    [onClose, handleSave],
  );

  return createPortal(
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={styles.sheet}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Note for ${habitName}`}
      >
        <div className={styles.header}>
          <span className={styles.title}>Note for {habitName}</span>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            <XIcon size={20} aria-hidden="true" />
          </button>
        </div>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a note about today's completion..."
          rows={4}
          maxLength={500}
        />
        <div className={styles.footer}>
          <span className={styles.hint}>Ctrl+Enter to save</span>
          <button type="button" className={styles.saveButton} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
